/*
  GAROE Moments, global behaviour: the mobile navigation drawer, the basket
  drawer, and auto-submitting category filters. Plain JavaScript, no build
  step, no dependencies. Everything uses event delegation or custom
  elements, so it keeps working when the theme editor re-renders a section.
*/
(() => {
  const config = window.GAROE || { routes: {}, strings: {} };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function lockScroll(locked) {
    document.body.classList.toggle('scroll-locked', locked);
  }

  /* Keeps Tab focus inside an open drawer. */
  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* Header basket count, both the desktop "Basket (n)" label and the mobile badge. */
  function updateCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
    });
    document.querySelectorAll('[data-cart-count-badge]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  }

  /* ---------- Mobile navigation ---------- */

  function mobileNavParts() {
    const nav = document.getElementById('MobileNav');
    return { nav, toggle: document.querySelector('[data-menu-toggle]') };
  }

  function openMobileNav() {
    const { nav, toggle } = mobileNavParts();
    if (!nav) return;
    nav.inert = false;
    nav.classList.add('is-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    lockScroll(true);
    const close = nav.querySelector('.mobile-nav__close');
    if (close) close.focus();
  }

  function closeMobileNav() {
    const { nav, toggle } = mobileNavParts();
    if (!nav || !nav.classList.contains('is-open')) return;
    nav.classList.remove('is-open');
    nav.inert = true;
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }
    lockScroll(false);
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-menu-toggle]')) {
      openMobileNav();
    } else if (event.target.closest('[data-menu-close]')) {
      closeMobileNav();
    }
  });

  document.addEventListener('keydown', (event) => {
    const { nav } = mobileNavParts();
    if (!nav || !nav.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      closeMobileNav();
    } else {
      trapFocus(nav, event);
    }
  });

  /* ---------- Basket drawer ---------- */

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-cart-close]')) {
          event.preventDefault();
          this.close();
          return;
        }
        const change = event.target.closest('[data-cart-change]');
        if (change) {
          event.preventDefault();
          this.changeLine(change.dataset.key, Number(change.dataset.quantity));
        }
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.close();
        } else {
          trapFocus(this.panel, event);
        }
      });
    }

    get panel() {
      return this.querySelector('[data-cart-panel]');
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.inert = false;
      this.classList.add('is-open');
      lockScroll(true);
      requestAnimationFrame(() => this.panel.focus());
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.inert = true;
      lockScroll(false);
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    }

    /* Swaps in freshly rendered drawer markup from the Section Rendering API. */
    renderSection(html) {
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
      if (!fresh) return;
      const freshPanel = fresh.querySelector('[data-cart-panel]');
      if (freshPanel) this.panel.innerHTML = freshPanel.innerHTML;
      updateCounts(Number(fresh.dataset.itemCount || 0));
    }

    async changeLine(key, quantity) {
      this.classList.add('is-busy');
      try {
        const response = await fetch(`${config.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: 'cart-drawer',
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || !data.sections) throw new Error(data.description || data.message);
        this.renderSection(data.sections['cart-drawer']);
        this.panel.focus();
      } catch (error) {
        // Fall back to the full basket page rather than leave the drawer stale.
        window.location.href = config.routes.cart;
      } finally {
        this.classList.remove('is-busy');
      }
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cart-toggle]');
    if (!toggle || document.body.classList.contains('template-cart')) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    drawer.open(toggle);
  });

  /* ---------- Category filters: apply on change ---------- */

  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-filter-form]');
    if (!form) return;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-filter-form]');
    if (!form) return;
    // Leave empty price boxes out of the URL.
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      if (input.value === '') input.disabled = true;
    });
  });

  window.GAROE = Object.assign(config, { updateCounts });
})();
